import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Grid from 'react-virtualized/dist/commonjs/Grid';

import cellRangeRenderer from './cellRangeRenderer';

/**
 * A wrapper of the Grid for internal only
 */
class GridTable extends React.PureComponent {
  constructor(props) {
    super(props);

    this._setHeaderRef = this._setHeaderRef.bind(this);
    this._setBodyRef = this._setBodyRef.bind(this);
    this._headerRowHeight = this._headerRowHeight.bind(this);
    this._rowHeight = this._rowHeight.bind(this);
    this.renderHeaderRow = this.renderHeaderRow.bind(this);
    this.renderRow = this.renderRow.bind(this);
    this._handleSectionRendered = this._handleSectionRendered.bind(this);
  }

  _setHeaderRef(ref) {
    this.headerRef = ref;
  }

  _setBodyRef(ref) {
    this.bodyRef = ref;
  }

  _headerRowHeight({ index }) {
    const headerHeight = this.props.hideHeader ? 0 : this.props.headerHeight;
    return index === 0 ? headerHeight : this.props.rowHeight({ index: index - 1 });
  }

  _rowHeight({ index }) {
    return this.props.rowHeight({ index: index + this.props.frozenRowCount });
  }

  forceUpdateTable() {
    this.headerRef && this.headerRef.forceUpdate();
    this.bodyRef && this.bodyRef.forceUpdate();
  }

  getFrozenRowsHeight() {
    let frozenRowsHeight = 0;
    for (let i = 0; i < this.props.frozenRowCount; i++) {
      frozenRowsHeight += this.props.rowHeight({ index: i });
    }
    return frozenRowsHeight;
  }

  getTotalColumnsWidth() {
    if (!this.bodyRef) return 0;
    // for versions above 9.18.5
    if (this.bodyRef.getTotalColumnsWidth) {
      return this.bodyRef.getTotalColumnsWidth();
    }
    // for version 9.18.5 and below
    if (this.bodyRef._columnSizeAndPositionManager) {
      return this.bodyRef._columnSizeAndPositionManager.getTotalSize();
    }
    // should never reach here, just in case for too old versions
    return 0;
  }

  getTotalRowsHeight() {
    const frozenRowsHeight = this.getFrozenRowsHeight();

    if (!this.bodyRef) return frozenRowsHeight;
    // for versions above 9.18.5
    if (this.bodyRef.getTotalRowsHeight) {
      return this.bodyRef.getTotalRowsHeight() + frozenRowsHeight;
    }
    // for version 9.18.5 and below
    if (this.bodyRef._rowSizeAndPositionManager) {
      return this.bodyRef._rowSizeAndPositionManager.getTotalSize() + frozenRowsHeight;
    }
    // should never reach here, just in case for too old versions
    return frozenRowsHeight;
  }

  measureAllRows() {
    this.headerRef && this.headerRef.measureAllCells();
    this.bodyRef && this.bodyRef.measureAllCells();
  }

  recomputeRowHeights(startRowIndex = 0) {
    if (startRowIndex < this.props.frozenRowCount) {
      this.headerRef &&
        this.headerRef.recomputeGridSize({
          rowIndex: startRowIndex + 1,
          columnIndex: 0
        });
      this.bodyRef &&
        this.bodyRef.recomputeGridSize({
          rowIndex: 0,
          columnIndex: 0
        });
      // we have to forceUdpate to make the new headerHeight takes effect
      this.forceUpdate();
    } else {
      this.bodyRef &&
        this.bodyRef.recomputeGridSize({
          rowIndex: startRowIndex - this.props.frozenRowCount,
          columnIndex: 0
        });
    }
  }

  scrollToPosition(args) {
    this.headerRef && this.headerRef.scrollToPosition({ scrollLeft: args.scrollLeft });
    this.bodyRef && this.bodyRef.scrollToPosition(args);
  }

  scollToTop(scrollTop) {
    this.bodyRef && this.bodyRef.scrollToPosition({ scrollTop });
  }

  scrollToLeft(scrollLeft) {
    this.headerRef && this.headerRef.scrollToPosition({ scrollLeft });
    this.bodyRef && this.bodyRef.scrollToPosition({ scrollLeft });
  }

  scrollToRow(rowIndex = 0) {
    if (rowIndex < this.props.frozenRowCount) return;
    this.bodyRef &&
      this.bodyRef.scrollToCell({
        rowIndex: rowIndex - this.props.frozenRowCount,
        columnIndex: 0
      });
  }

  renderHeaderRow(args) {
    if (args.rowIndex === 0) {
      if (this.props.hideHeader) return null;
      return this.props.renderHeader({ ...args, columns: this.props.columns });
    }
    const rowIndex = args.rowIndex - 1;
    const rowData = this.props.data[rowIndex];
    return this.props.renderRow({ ...args, columns: this.props.columns, rowData, rowIndex });
  }

  renderRow(args) {
    const rowIndex = args.rowIndex + this.props.frozenRowCount;
    const rowData = this.props.data[rowIndex];
    return this.props.renderRow({ ...args, columns: this.props.columns, rowData, rowIndex });
  }

  render() {
    const {
      containerStyle,
      className,
      data,
      frozenRowCount,
      width,
      height,
      hideHeader,
      headerWidth,
      bodyWidth,
      onScroll,
      onScrollbarPresenceChange,
      ...rest
    } = this.props;
    const headerHeight = hideHeader ? 0 : this.props.headerHeight;
    const frozenRowsHeight = this.getFrozenRowsHeight();
    const cls = cn('BaseTable__table', className);
    const containerProps = containerStyle ? { style: containerStyle } : null;
    return (
      <div className={cls} {...containerProps}>
        {headerHeight + frozenRowsHeight > 0 && (
          <Grid
            {...rest}
            className="BaseTable__header"
            ref={this._setHeaderRef}
            width={width}
            height={Math.min(headerHeight + frozenRowsHeight, height)}
            rowHeight={this._headerRowHeight}
            rowCount={1 + frozenRowCount}
            columnWidth={headerWidth}
            columnCount={1}
            cellRenderer={this.renderHeaderRow}
            cellRangeRenderer={cellRangeRenderer}
          />
        )}
        <Grid
          {...rest}
          className="BaseTable__body"
          ref={this._setBodyRef}
          width={width}
          height={Math.max(height - headerHeight - frozenRowsHeight, 0)}
          rowHeight={this._rowHeight}
          rowCount={data.length - frozenRowCount}
          columnWidth={bodyWidth}
          columnCount={1}
          cellRenderer={this.renderRow}
          cellRangeRenderer={cellRangeRenderer}
          onScroll={onScroll}
          onSectionRendered={this._handleSectionRendered}
          onScrollbarPresenceChange={onScrollbarPresenceChange}
        />
      </div>
    );
  }

  _handleSectionRendered({
    rowOverscanStartIndex,
    rowOverscanStopIndex,
    rowStartIndex,
    rowStopIndex
  }) {
    const { onRowsRendered } = this.props;

    onRowsRendered({
      overscanStartIndex: rowOverscanStartIndex,
      overscanStopIndex: rowOverscanStopIndex,
      startIndex: rowStartIndex,
      stopIndex: rowStopIndex
    });
  }
}

GridTable.propTypes = {
  containerStyle: PropTypes.object,
  className: PropTypes.string,
  hideHeader: PropTypes.bool,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  headerHeight: PropTypes.number.isRequired,
  headerWidth: PropTypes.number.isRequired,
  bodyWidth: PropTypes.number.isRequired,
  rowHeight: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  frozenRowCount: PropTypes.number.isRequired,

  setHeaderRef: PropTypes.func,
  setBodyRef: PropTypes.func,
  onScroll: PropTypes.func,
  onRowsRendered: PropTypes.func,
  onScrollbarPresenceChange: PropTypes.func,
  renderHeader: PropTypes.func.isRequired,
  renderRow: PropTypes.func.isRequired
};

export default GridTable;
